/**
 * Product Compare Functionality
 * Manages product comparison feature including local storage, UI updates, and navigation
 */

class ProductCompare {
    constructor() {
        this.compareProducts = JSON.parse(
            localStorage.getItem("compareProducts") || "[]"
        );
        this.maxProducts = 4;
        this.init();
    }

    init() {
        this.bindEvents();
        this.updateToolbar();
        this.updateCheckboxes();
        this.updateButtonsVisibility();
        this.bindPageVisibilityEvents();
    }

    bindEvents() {
        // Handle compare checkbox clicks
        document.addEventListener("change", (e) => {
            if (
                e.target.classList.contains(
                    "product-compare-checkbox-input"
                )
            ) {
                this.handleCheckboxChange(e.target);
            }
        });

        // Handle remove product from compare toolbar
        document.addEventListener("click", (e) => {
            if (
                e.target.classList.contains(
                    "compare-remove-btn"
                )
            ) {
                const productId =
                    e.target.dataset.productId;
                this.removeProduct(productId);
            }
        });

        // Handle clear all comparison
        document.addEventListener("click", (e) => {
            if (
                e.target.classList.contains(
                    "compare-clear-btn"
                )
            ) {
                this.clearAll();
            }
        });

        // Handle compare button click
        document.addEventListener("click", (e) => {
            if (
                e.target.classList.contains(
                    "compare-btn"
                ) &&
                !e.target.disabled
            ) {
                e.preventDefault();
                this.goToComparePage();
            }
        });

        // Handle remove product from compare page
        document.addEventListener("click", (e) => {
            if (
                e.target.classList.contains(
                    "compare-remove-product"
                )
            ) {
                const productId =
                    e.target.dataset.productId;
                this.removeProduct(productId);
                // Reload the compare page to update the table
                if (
                    window.location.pathname.includes(
                        "/pages/compare-products"
                    )
                ) {
                    window.location.reload();
                }
            }
        });
    }

    bindPageVisibilityEvents() {
        // Listen for page visibility changes (when user switches tabs or returns to page)
        document.addEventListener(
            "visibilitychange",
            () => {
                if (!document.hidden) {
                    // Page became visible, sync the state
                    this.syncWithStorage();
                }
            }
        );

        // Listen for page focus (when user returns to this window)
        window.addEventListener("focus", () => {
            this.syncWithStorage();
        });

        // Listen for storage changes from other tabs/windows
        window.addEventListener("storage", (e) => {
            if (e.key === "compareProducts") {
                this.syncWithStorage();
            }
        });

        // Listen for browser navigation (back/forward buttons)
        window.addEventListener("popstate", () => {
            // Small delay to ensure page has loaded
            setTimeout(() => {
                this.syncWithStorage();
            }, 50);
        });
    }

    syncWithStorage() {
        // Re-read from localStorage to sync state
        const storedProducts = localStorage.getItem(
            "compareProducts"
        );
        this.compareProducts = JSON.parse(
            storedProducts || "[]"
        );
        this.updateToolbar();
        this.updateCheckboxes();
        this.updateButtonsVisibility();
    }

    handleCheckboxChange(checkbox) {
        const productCard =
            checkbox.closest("product-card");
        const productId = checkbox.dataset.productId;
        const productHandle =
            checkbox.dataset.productHandle;
        const productTitle = checkbox.dataset.productTitle;
        const productImage = checkbox.dataset.productImage;
        const productUrl = checkbox.dataset.productUrl;

        if (checkbox.checked) {
            this.addProduct({
                id: productId,
                handle: productHandle,
                title: productTitle,
                image: productImage,
                url: productUrl,
            });
        } else {
            this.removeProduct(productId);
        }
    }

    addProduct(product) {
        // Check if product already exists
        if (
            this.compareProducts.find(
                (p) => p.id === product.id
            )
        ) {
            return;
        }

        // Check maximum limit
        if (
            this.compareProducts.length >= this.maxProducts
        ) {
            const translations =
                window.theme?.translations?.compare || {};
            alert(
                translations.maximum_four_products ||
                    "Maximum 4 products can be compared"
            );
            // Uncheck the checkbox
            const checkbox = document.querySelector(
                `[data-product-id="${product.id}"]`
            );
            if (checkbox) checkbox.checked = false;
            return;
        }

        // Add product to comparison
        this.compareProducts.push(product);
        this.saveToStorage();
        this.updateToolbar();
        this.updateCheckboxes();
        this.updateButtonsVisibility();
        this.showNotification("add", product.title);
    }

    removeProduct(productId) {
        // Find the product before removing it to show in notification
        const product = this.compareProducts.find(
            (p) => p.id === productId
        );

        this.compareProducts = this.compareProducts.filter(
            (p) => p.id !== productId
        );
        this.saveToStorage();
        this.updateToolbar();
        this.updateCheckboxes();
        this.updateButtonsVisibility();

        this.showNotification(
            "remove",
            product?.title || ""
        );

        // If on compare page and no products left, reload to show empty state
        if (
            window.location.pathname.includes(
                "/pages/compare-products"
            ) &&
            this.compareProducts.length === 0
        ) {
            setTimeout(() => {
                window.location.reload();
            }, 500); // Small delay to show notification first
        }
    }

    clearAll() {
        this.compareProducts = [];
        this.saveToStorage();
        this.updateToolbar();
        this.updateCheckboxes();
        this.updateButtonsVisibility();
        this.showNotification("clear");

        // If on compare page, reload to show empty state
        if (
            window.location.pathname.includes(
                "/pages/compare-products"
            )
        ) {
            setTimeout(() => {
                window.location.reload();
            }, 500); // Small delay to show notification first
        }
    }

    saveToStorage() {
        localStorage.setItem(
            "compareProducts",
            JSON.stringify(this.compareProducts)
        );
    }

    updateToolbar() {
        // Don't show toolbar on compare products page
        if (
            window.location.pathname.includes(
                "/pages/compare-products"
            )
        ) {
            const toolbar = document.querySelector(
                ".compare-toolbar"
            );
            if (toolbar) {
                toolbar.classList.remove("show");
            }
            return;
        }

        let toolbar = document.querySelector(
            ".compare-toolbar"
        );

        if (this.compareProducts.length === 0) {
            if (toolbar) {
                toolbar.classList.remove("show");
            }
            return;
        }

        // Create toolbar if it doesn't exist
        if (!toolbar) {
            toolbar = this.createToolbar();
            document.body.appendChild(toolbar);
        }

        // Update toolbar content
        this.updateToolbarContent(toolbar);

        // Show toolbar
        toolbar.classList.add("show");
    }

    createToolbar() {
        const toolbar = document.createElement("div");
        toolbar.className = "compare-toolbar";

        // Get translations
        const translations =
            window.theme?.translations?.compare || {};
        const clearAllText =
            translations.clear_all || "Clear all";
        const compareNowText =
            translations.compare_now || "Compare now";

        toolbar.innerHTML = `
            <div class="compare-toolbar-content">
                <ul class="compare-products-list"></ul>
                <div class="compare-actions">
                <span class="compare-count">0 selected</span>
                <button class="compare-clear-btn">${clearAllText}</button>
                <button class="compare-btn" disabled>${compareNowText}</button>
                </div>
            </div>
        `;
        return toolbar;
    }

    updateToolbarContent(toolbar) {
        const productsList = toolbar.querySelector(
            ".compare-products-list"
        );
        const compareCount = toolbar.querySelector(
            ".compare-count"
        );
        const compareBtn =
            toolbar.querySelector(".compare-btn");

        // Update products list
        productsList.innerHTML = this.compareProducts
            .map(
                (product) => `
                    <li class="compare-product-item">
                        <img src="${product.image}" alt="${product.title}" loading="lazy" style="object-fit: contain;">
                        <span class="compare-product-name">${product.title}</span>
                        <button class="compare-remove-btn" data-product-id="${product.id}" title="Remove">×</button>
                    </li>
                `
            )
            .join("");

        // Update count
        const count = this.compareProducts.length;
        const translations =
            window.theme?.translations?.compare || {};
        const selectedText =
            translations.selected || "selected";
        compareCount.textContent = `${count} ${selectedText}`;

        // Update compare button state
        if (count >= 2) {
            compareBtn.disabled = false;
            compareBtn.classList.remove("disabled");
        } else {
            compareBtn.disabled = true;
            compareBtn.classList.add("disabled");
        }
    }

    updateCheckboxes() {
        // Update all checkboxes on the page
        document
            .querySelectorAll(
                ".product-compare-checkbox-input"
            )
            .forEach((checkbox) => {
                const productId =
                    checkbox.dataset.productId;
                const isSelected =
                    this.compareProducts.some(
                        (p) => p.id === productId
                    );
                const container = checkbox.closest(
                    ".product-compare-checkbox"
                );

                checkbox.checked = isSelected;

                if (container) {
                    container.classList.toggle(
                        "checked",
                        isSelected
                    );
                }
            });
    }

    showNotification(action, productTitle) {
        // Get translations
        const translations =
            window.theme?.translations?.compare || {};
        let message = "";

        if (action === "add") {
            message =
                translations.notification_added?.replace(
                    "{{ product_title }}",
                    productTitle
                ) || `${productTitle} added to comparison`;
        } else if (action === "remove") {
            message =
                translations.notification_removed?.replace(
                    "{{ product_title }}",
                    productTitle
                ) ||
                `${productTitle} removed from comparison`;
        } else if (action === "clear") {
            message =
                translations.notification_cleared ||
                "All products removed from comparison";
        }

        // Use the unified notification toast system
        if (window.NotificationToast) {
            window.NotificationToast.show(message, "info");
        } else {
            // Fallback for when NotificationToast is not available
            console.log("Notification:", message);
        }
    }

    goToComparePage() {
        if (this.compareProducts.length < 2) {
            const translations =
                window.theme?.translations?.compare || {};
            alert(
                translations.select_at_least_two ||
                    "Please select at least two products to compare"
            );
            return;
        } else {
            window.location.href =
                "/pages/compare-products";
        }
    }

    // Get products for compare page
    getCompareProducts() {
        return this.compareProducts;
    }

    // Calculate financing quote (example implementation)
    calculateFinancingQuote(price, months = 3) {
        const monthlyPayment = price / months;
        return monthlyPayment.toFixed(2);
    }

    updateButtonsVisibility() {
        // If on compare products page, always show the buttons
        if (
            window.location.pathname.includes(
                "/pages/compare-products"
            )
        ) {
            const showButtons = () => {
                const chatButton =
                    document.querySelector("#shopify-chat");
                const rewardsButton =
                    document.querySelector(
                        "#smile-ui-lite-container"
                    );

                if (chatButton)
                    chatButton.style.display = "block";
                if (rewardsButton)
                    rewardsButton.style.display = "block";
            };

            // Initial show
            showButtons();
            return;
        }

        // Function to check and hide buttons
        const checkAndHideButtons = () => {
            const chatButton =
                document.querySelector("#shopify-chat");
            const rewardsButton = document.querySelector(
                "#smile-ui-lite-container"
            );

            if (chatButton)
                chatButton.style.display = "none";
            if (rewardsButton)
                rewardsButton.style.display = "none";

            // Return true if both buttons are found and hidden
            return chatButton && rewardsButton;
        };

        // If have selected products and not on compare page, hide the buttons
        if (this.compareProducts.length > 0) {
            // Initial hide attempt
            checkAndHideButtons();

            // Set up interval to check and hide every 100ms for 3 seconds
            let attempts = 0;
            const maxAttempts = 30; // 3 seconds (30 * 100ms)
            const interval = setInterval(() => {
                const buttonsFound = checkAndHideButtons();
                attempts++;

                // Clear interval if either max attempts reached or both buttons found and hidden
                if (
                    attempts >= maxAttempts ||
                    buttonsFound
                ) {
                    clearInterval(interval);
                }
            }, 100);
        } else {
            // If no selected products, show the buttons
            const showButtons = () => {
                const chatButton =
                    document.querySelector("#shopify-chat");
                const rewardsButton =
                    document.querySelector(
                        "#smile-ui-lite-container"
                    );

                if (chatButton)
                    chatButton.style.display = "block";
                if (rewardsButton)
                    rewardsButton.style.display = "block";

                // Return true if both buttons are found and shown
                return chatButton && rewardsButton;
            };

            // Initial show attempt
            showButtons();

            // Set up interval to check and show every 100ms for 3 seconds
            let attempts = 0;
            const maxAttempts = 30;
            const interval = setInterval(() => {
                const buttonsFound = showButtons();
                attempts++;

                // Clear interval if either max attempts reached or both buttons found and shown
                if (
                    attempts >= maxAttempts ||
                    buttonsFound
                ) {
                    clearInterval(interval);
                }
            }, 100);
        }
    }
}

// Initialize when DOM is loaded
document.addEventListener("DOMContentLoaded", () => {
    window.ProductCompare = new ProductCompare();

    // Additional sync after a short delay to ensure all checkboxes are rendered
    setTimeout(() => {
        if (window.ProductCompare) {
            window.ProductCompare.syncWithStorage();
        }
    }, 100);
});

// Export for use in other scripts
if (typeof module !== "undefined" && module.exports) {
    module.exports = ProductCompare;
}
